import { combineLatest, map, type Observable } from 'rxjs';
import { uniq } from 'lodash';
import { Features, hasFeature } from '@ardoq/features';
import {
  BooleanOperator,
  isVisualizationSlide,
  Operator,
  SearchContext,
} from '@ardoq/api-types';
import type {
  APIComponentAttributes,
  APICurrentUser,
  APISlideAttributes,
  ArdoqId,
  EnhancedPresentation,
  GenericQueryBuilderRule,
  QueryBuilderQuery,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import type { I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import {
  loadedPresentation$,
  type LoadedPresentationState,
} from 'presentation/streams/loadedPresentation$';
import {
  currentUser$,
  isLoadedCurrentUser,
} from 'streams/currentUser/currentUser$';
import { getContextSwitchEnabled } from 'streams/presentations/contextSwitchSetting';
import {
  getStartComponentTypes,
  isContextualSlide,
} from 'streams/presentations/contextSwitchTypes';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';

type ContextPickerViewModel = {
  search: { typeName: string; query: QueryBuilderQuery } | null;
  contextComponentId: ArdoqId | null;
  contextName: string | null;
  appliedSummary: { applied: number; total: number } | null;
};

/** Aggregated scope data may carry the type name as `typeName` instead of `type`. */
type ScopeComponent = APIComponentAttributes & { typeName?: string };

type ScopeComponentSummary = {
  typeId: ArdoqId;
  type: string | undefined;
  name: string | undefined;
};

const toScopeComponentById = ({
  traversalExecute,
}: EnhancedPresentation): Record<ArdoqId, ScopeComponentSummary> =>
  Object.fromEntries(
    Object.values(traversalExecute ?? {})
      .flatMap(({ loadedStates }) => loadedStates ?? [])
      .flatMap(({ scopeData }) => scopeData?.components ?? [])
      .map(({ _id, typeId, type, typeName, name }: ScopeComponent) => [
        _id,
        { typeId, type: type ?? typeName, name },
      ])
  );

const getWorkspaceIds = (slide: APISlideAttributes): ArdoqId[] =>
  isVisualizationSlide(slide)
    ? [slide.workspaceId, ...(slide.workspaceIds ?? [])].filter(isTruthy)
    : [];

const equalsRule = (field: string, value: string): GenericQueryBuilderRule => ({
  id: field,
  field,
  input: 'text',
  type: 'string',
  operator: Operator.EQUAL,
  value,
});

const toContextComponentQuery = (
  typeId: ArdoqId,
  workspaceIds: ArdoqId[]
): QueryBuilderQuery => ({
  condition: BooleanOperator.AND,
  rules: [
    {
      field: 'type',
      id: 'type',
      input: 'select',
      operator: Operator.EQUAL,
      type: 'string',
      value: SearchContext.COMPONENT,
    },
    {
      condition: BooleanOperator.AND,
      rules: [
        equalsRule('typeId', typeId),
        {
          condition: BooleanOperator.OR,
          rules: workspaceIds.map(id => equalsRule('rootWorkspace', id)),
        },
      ],
    },
  ],
});

const toSearch = (
  slides: APISlideAttributes[],
  scopeComponentById: Record<ArdoqId, ScopeComponentSummary>
): ContextPickerViewModel['search'] => {
  const [contextType, ...otherTypes] = getStartComponentTypes(
    slides,
    scopeComponentById
  );
  if (!contextType?.type || otherTypes.length > 0) return null;
  const workspaceIds = uniq(slides.flatMap(getWorkspaceIds));
  return {
    typeName: contextType.type,
    query: toContextComponentQuery(contextType.typeId, workspaceIds),
  };
};

type ContextNameSources = {
  scopeComponentById: Record<ArdoqId, ScopeComponentSummary>;
  componentsById: Partial<Record<ArdoqId, APIComponentAttributes>>;
};

const toContextName = (
  { scopeComponentById, componentsById }: ContextNameSources,
  contextComponentId: ArdoqId | null
): string | null => {
  if (!contextComponentId) return null;
  const scopeName = scopeComponentById[contextComponentId]?.name;
  const name = isTruthy(scopeName)
    ? scopeName
    : componentsById[contextComponentId]?.name;
  return isTruthy(name) ? name : null;
};

export const toContextPickerViewModel = (
  { presentation, contextComponentId }: LoadedPresentationState,
  currentUser: APICurrentUser | null,
  hasParameterizedPresentations: boolean
): ContextPickerViewModel | null => {
  if (
    !presentation ||
    !hasParameterizedPresentations ||
    !isLoadedCurrentUser(currentUser) ||
    !getContextSwitchEnabled(presentation)
  ) {
    return null;
  }
  const slides = (presentation.authorizedSlides ?? []).filter(
    isContextualSlide
  );
  const scopeComponentById = toScopeComponentById(presentation);
  const search = toSearch(slides, scopeComponentById);
  if (slides.length === 0 || (!search && !contextComponentId)) return null;
  const { appliedContext } = presentation;

  return {
    search,
    contextComponentId,
    contextName: toContextName(
      { scopeComponentById, componentsById: presentation.componentsById },
      contextComponentId
    ),
    appliedSummary:
      appliedContext && appliedContext.componentId !== contextComponentId
        ? null
        : {
            applied: appliedContext?.slideIds.length ?? 0,
            total: slides.length,
          },
  };
};

export type ContextPickerProps = {
  picker: ContextPickerViewModel | null;
  locale: Locale;
  i18nLocale: I18nLocale;
};

export const contextPicker$: Observable<ContextPickerProps> = combineLatest([
  loadedPresentation$,
  currentUser$,
  locale$,
  i18nLocale$,
]).pipe(
  map(([loaded, currentUser, locale, i18nLocale]) => ({
    picker: toContextPickerViewModel(
      loaded,
      currentUser,
      hasFeature(Features.PARAMETERIZED_PRESENTATIONS)
    ),
    locale,
    i18nLocale,
  }))
);
